import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  Typography,
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  ListSubheader,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  DirectionsBus as BusIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  AccountBalanceWallet as WalletIcon,
  Assessment as ReportIcon,
  Route as RouteIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import Header from './Header';
import { useRoleAccess } from '../../hooks/useRoleAccess';

/**
 * Layout principal con Sidebar filtrado por roles - ACTUALIZADO CON SISTEMA DE REDONDOS
 * Path: backoffice-web/src/components/Layout/Layout.js
 * Última modificación: 2025-01-02 07:00
 * Dev: cpalacios01@gmail.com | +595994648273
 */

const drawerWidth = 240;

const allMenuItems = [
  // Dashboard
  { 
    text: 'Dashboard', 
    icon: <DashboardIcon />, 
    path: '/',
    section: null,
  },
  
  // ========================================
  // SISTEMA DE REDONDOS (NUEVO)
  // ========================================
  {
    section: 'Sistema de Redondos',
  },
  { 
    text: 'Redondos', 
    icon: <BusIcon />, 
    path: '/redondos',
    section: null,
  },
  { 
    text: 'Itinerarios', 
    icon: <RouteIcon />, 
    path: '/itinerarios',
    section: null,
  },
  { 
    text: 'Gastos', 
    icon: <ReceiptIcon />, 
    path: '/gastos',
    section: null,
  },
  
  // ========================================
  // GESTIÓN
  // ========================================
  {
    section: 'Gestión',
  },
  { 
    text: 'Choferes', 
    icon: <PeopleIcon />, 
    path: '/choferes',
    section: null,
  },
  { 
    text: 'Buses', 
    icon: <BusIcon />, 
    path: '/buses',
    section: null,
  },
  
  // ========================================
  // CONFIGURACIÓN
  // ========================================
  {
    section: 'Configuración',
  },
  { 
    text: 'Tarifas', 
    icon: <MoneyIcon />, 
    path: '/tarifas',
    section: null,
  },
  { 
    text: 'Usuarios', 
    icon: <PersonIcon />, 
    path: '/usuarios',
    section: null,
  },
  
  // ========================================
  // OTROS
  // ========================================
  {
    section: 'Otros',
  },
  { 
    text: 'Cajas', 
    icon: <WalletIcon />, 
    path: '/cajas',
    section: null,
  },
  { 
    text: 'Reportes', 
    icon: <ReportIcon />, 
    path: '/reportes',
    section: null,
  },
];

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { canViewMenu } = useRoleAccess();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Filtrar menús según permisos del rol
  const menuItems = allMenuItems.filter(item => {
    // Si es un separador de sección, verificar si hay items visibles en esa sección
    if (item.section) {
      // Buscar si hay al menos un item visible después de esta sección
      const currentIndex = allMenuItems.indexOf(item);
      const nextItems = allMenuItems.slice(currentIndex + 1);
      const hasVisibleItems = nextItems.some(nextItem => {
        if (nextItem.section) return false; // Detener en la siguiente sección
        return canViewMenu(nextItem.text);
      });
      return hasVisibleItems;
    }
    // Si es un item normal, verificar permisos
    return canViewMenu(item.text);
  });

  const drawer = (
    <div>
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BusIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h6" noWrap component="div">
            Transporte TLG
          </Typography>
        </Box>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item, index) => {
          // Si es un separador de sección
          if (item.section) {
            return (
              <ListSubheader
                key={`section-${index}`}
                component="div"
                sx={{
                  bgcolor: 'transparent',
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                  mt: index === 0 ? 0 : 2,
                }}
              >
                {item.section}
              </ListSubheader>
            );
          }
          
          // Si es un item normal
          const isActive = location.pathname === item.path;
          return (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={isActive}
                onClick={() => {
                  navigate(item.path);
                  if (mobileOpen) {
                    setMobileOpen(false);
                  }
                }}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: 'primary.light',
                    '&:hover': {
                      backgroundColor: 'primary.light',
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ color: isActive ? 'primary.main' : 'inherit' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Header mejorado con reloj */}
      <Header onMenuClick={handleDrawerToggle} />

      {/* Sidebar */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Contenido principal */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
}

// cpalacios01@gmail.com | +595994648273
